<template>
  <Modal
    v-if="showModal.confirmAnswer"
    size="md"
    confirmText=""
    cancelText=""
  >
    <div class="text-center">
      <h3 class="text-t4 mb-1">Svara på erbjudande</h3>

      <p
        v-if="_offer.rentalObject.objectType === RENTALOBJECT_TYPES.STUDENT_APARTMENT"
        class="text-b1"
        v-html="webapp_config.msg.offer_student_info_answering_yes"
      ></p>
      <p v-else class="text-b1" v-html="webapp_config.msg.offer_other_info_answering_yes" />
      <p
        v-if="_offer.rentalObject.objectType === RENTALOBJECT_TYPES.STUDENT_APARTMENT"
        class="text-b1"
        v-html="webapp_config.msg.offer_student_info_answering_no"
      ></p>
      <p v-else class="text-b1" v-html="webapp_config.msg.offer_other_info_answering_no" />
    </div>
    <template #footer>
      <div class="flex gap-2">
        <Button variant="tertiery" @click="showModal.confirmAnswer = false">Avbryt</Button>
        <Button variant="secondary" @click="answerNo">Svara Nej</Button>
        <Button v-if="!hasAnswered" variant="primary" @click="answerYes">Svara Ja</Button>
      </div>
    </template>
  </Modal>

  <Modal
    v-if="showModal.information"
    cancelText="Stäng"
    size="md"
    v-on:cancel="showModal.information = false"
  >
    <h3 class="text-t4 mb-1">Kontakt för visning</h3>
    <div v-if="displayTimes.length == 0">
      <p class="text-b1">Det finns inga tider för visning</p>
    </div>
    <div v-else>
      <div v-for="(displayTime, index) in displayTimes">
        <h4 class="text-t5 mb-1">Kontaktperson</h4>
        <p class="text-b1">{{ displayTime.name }}</p>

        <h4 class="text-t5 mb-1">Telefon</h4>
        <p class="text-b1">{{ displayTime.phone }}</p>
        <p class="text-b1">{{ displayTime.mobile }}</p>
        <p class="text-b1">{{ displayTime.displayNote }}</p>
        <a
          v-if="displayTime.address && displayTime.address.indexOf('http') > -1"
          :href="displayTime.address"
        >
          Boka visning
        </a>
      </div>
    </div>
  </Modal>
  <div class="relative rounded-[10px] border border-solid border-gray-light bg-white mb-8">
    <div class="text-b2 absolute -top-4 right-6 rounded bg-purple-20 px-4 py-1">
      Du har plats {{ order }}
    </div>
    <div class="grid grid-cols-1 gap-6 p-4 md:p-6 md:grid-cols-[3fr_2fr]">
      <div class="md:grid grid-cols-3 gap-4 md:gap-x-6 md:gap-y-2">
        <div class="row-span-2 aspect-square h-[70px] w-[70px] md:h-auto md:w-full md:row-span-4 float-left md:float-none mb-4 mr-4 md:mr-0 md:mb-0">
          <img
            v-if="rentalObject.images.length > 0"
            :src="rentalObject.images[0].url"
            alt="Bostad"
            class="h-full w-full rounded-[10px] object-cover"
          />
        </div>
        <div class="col-span-2 flex flex-col gap-2">
          <h3 class="text-t6">{{ rentalObject.postalAddress }}</h3>
          <p class="text-b2">{{ rentalObject.neigbourhood }}</p>
        </div>
        <p
          class="text-label col-span-full flex w-fit items-center justify-center rounded-[2px] bg-primary-30 px-2 py-1 uppercase md:col-span-2 clear-both"
        >
          {{ getStatus(offer) }}
        </p>
        <div class="text-b2 col-span-full md:col-span-2 mt-2 md:mt-0">
          <p v-if="offer && !lastReplyHasPast">
            Sista dag för att svara:
            <span class="font-thick">{{ formatDate(lastReplyDate) }}</span>
          </p>

          <p v-if="lastReplyHasPast">
            Anmälningtiden gick ut
            <span class="font-thick">{{ formatDate(lastReplyDate) }}.</span>
          </p>

          <div v-if="hasAnswered">
            <p v-if="offer.responseCode === OFFER_RESPONSE_CODES.YES">
              Du har svarat
              <span class="font-thick">Ja</span>
            </p>
            <p v-if="offer.responseCode === OFFER_RESPONSE_CODES.NO">
              Du har svarat
              <span class="font-thick">Nej</span>
            </p>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <p class="text-b2 md:text-b3">Nästa steg</p>
        <ul
          class="flex flex-col gap-2"
          v-if="hasAnswered && responseCode === OFFER_RESPONSE_CODES.NO"
        >
          <li class="flex gap-2">
            <div class="size-5">
              <IconCheckmark />
            </div>
            <p class="text-b2 md:text-b3">Tänk på att du bara kan tacka nej till fem erbjudanden under en 30-dagarsperiod.</p>
          </li>
        </ul>
        <ul class="flex flex-col gap-2" v-if="!answerIsReplyable">
          <li class="flex gap-2">
            <div class="size-5">
              <IconCheckmark />
            </div>
            <p class="text-b2 md:text-b3">
              Vi kontaktar i turordning de som står i kö och har svarat ja för att kontrollera att
              de uppfyller kraven för att få hyra och skriva avtal.
            </p>
          </li>
        </ul>
        <ul
          class="flex flex-col gap-2"
          v-else-if="!lastReplyHasPast && responseCode !== OFFER_RESPONSE_CODES.NO"
        >
          <li class="flex gap-2">
            <div class="size-5">
              <IconCheckmark />
            </div>
            <p class="text-b2 md:text-b3">Gå på visning</p>
          </li>
          <li class="flex gap-2">
            <div class="size-5">
              <IconCheckmark />
            </div>
            <p class="text-b2 md:text-b3">Svara ja eller nej innan sista svarsdatum.</p>
          </li>
        </ul>
        <ul class="flex flex-col gap-2" v-else>
          <li class="flex gap-2">
            <div class="size-5">
              <IconCheckmark />
            </div>
            <p class="text-b2 md:text-b3">
              Vi handlägger och går igenom de inkomna intresseanmälningarna. De som står först på
              tur blir erbjuden visning.
            </p>
          </li>
        </ul>
      </div>
    </div>
    <div
      class="flex flex-col justify-end gap-2 px-4 md:px-6 py-4 md:flex-row"
      :class="{ 'border-t border-solid border-gray-light': responseCode !== OFFER_RESPONSE_CODES.NO }"
    >
      <Button
        v-if="responseCode !== OFFER_RESPONSE_CODES.NO"
        variant="secondary"
        size="large"
        :to="{
          name: 'OfferVacancy',
          params: { id: rentalObjectCode },
        }"
      >
        Läs mer om
        <span v-if="rentalObject.objectType == RENTALOBJECT_TYPES.APARTMENT">bostaden</span>
        <span v-if="rentalObject.objectType == RENTALOBJECT_TYPES.PARKING">parkeringsplatsen</span>
        <span v-if="rentalObject.objectType == RENTALOBJECT_TYPES.PREMISE">lokalen</span>
      </Button>
      <Button
        v-if="!lastReplyHasPast && responseCode !== OFFER_RESPONSE_CODES.NO"
        variant="secondary"
        size="large"
        @click="showModal.information = true"
      >
        Se visningsinformation
      </Button>
      <Button v-if="answerIsReplyable" size="large" @click="showModal.confirmAnswer = true">
        <span v-if="hasAnswered">Ändra svar</span>
        <span v-else>Svara</span>
      </Button>
    </div>
  </div>
</template>

<script>
import { dataLayerPush } from '@/helpers/datalayer'
import { mapGetters } from 'vuex';
import dayjs from 'dayjs';
import Modal from '@/components/ui/modal';
import Button from '@/components/ui/button';
import IconCheckmark from '@/components/icons/IconCheckmark';
import {
  RENTALOBJECT_TYPE_CODES,
  OFFER_RESPONSE_CODES,
  RENTALOBJECT_TYPES,
} from '@/config/constants';
import OfferMixin from '@/mixins/offer';

export default {
  mixins: [OfferMixin],
  props: {
    offer: {
      type: Object,
      required: false,
    },
    offerNumber: {
      type: Number,
      required: false,
    },
  },
  components: {
    Modal,
    Button,
    IconCheckmark,
  },
  data() {
    return {
      showLoader: false,
      showModal: {
        confirmAnswer: false,
        information: false,
      },
      failed: '',
      answered: '',
      success: false,

      OFFER_RESPONSE_CODES,
      RENTALOBJECT_TYPES,
      RENTALOBJECT_TYPE_CODES,
    };
  },
  computed: {
    ...mapGetters(['webapp_config']),
    _offer() {
      if (this.offer) return this.offer;
      else if (this.offerNumber) return this.$store.getters.getOfferByOfferNumber(this.offerNumber);
    },
  },
  methods: {
    formatDate(date) {
      return dayjs(date).format('YYYY-MM-DD');
    },
    getStatus(obj) {
      if (obj._lastReplyDatePast && obj.status == 'G') return 'HANDLÄGGS';
      else if (obj._lastReplyDatePast === false) return 'ERBJUDANDE OM VISNING';
      else if (obj.status.toLowerCase() == 'current' && !dayjs(obj.publishedTo).isBefore())
        return 'DU ÄR ANMÄLD';
      else return 'HANDLÄGGS';
    },
    /**
     * Skicka svar på erbjudandet "Ja"
     */
    answerYes() {
      this.showLoader = true;
      this.showModal.confirmYes = false;

      return this.$store
        .dispatch('postOfferAnswer', { id: this._offer.id, answerIsYes: true })
        .then(() => {
          this.success = true;
          this.showModal.confirmAnswer = false;
        })
        .catch((error) => {
          if (error.response.data.description == 'Not found')
            this.failed = 'Vi kunde inte hitta detta erbjudande';
          else if (error.response.data.description.includes('Tillåtet antal ja-svar'))
            this.failed =
              'Du har redan svarat ja på tre erbjudanden under den senaste 30-dagarsperioden';
          else this.failed = error.response.data.description;
          this.showModal.error = true;
        })
        .finally(() => {
          this.showLoader = false;
        });
    },

    /**
     * Skicka svar på erbjudandet "Nej"
     */
    answerNo() {
      this.showLoader = true;
      this.showModal.confirmNo = false;
      dataLayerPush({ event: 'decline-object' });
      this.answered = 'no';
      return this.$store
        .dispatch('postOfferAnswer', { id: this._offer.id, answerIsYes: false })
        .then(() => {
          this.success = true;
          if (this.rentalObject.objectType === RENTALOBJECT_TYPES.APARTMENT) {
            window.hj =
              window.hj ||
              function () {
                (hj.q = hj.q || []).push(arguments);
              };
            hj('event', 'decline-object');
          }

          this.showModal.confirmAnswer = false;
        })
        .catch((error) => {
          this.failed = error.response.data.description;
          this.showModal.error = true;
        })
        .finally(() => {
          this.showLoader = false;
        });
    },
  },
  created() {
    if (this.offerNumber) {
      this.$store.cache.dispatch('fetchOffers').finally(() => {
        this.showLoader = false;
      });
    } else if (this.offer) {
      this.showLoader = false;
    } else {
      console.error('offer-item.vue Varken offer eller invoiceId satt :(');

      this.showLoader = false;
    }
  },
};
</script>
